import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Typography,
  Card,
  Box,
  TableContainer,
  Container,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState, ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import FilterButtonWithPopOver from './buttons/filterButtonWithPopOver';
import ReportPreview from './reportPreview/reportPreview';

import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';
import { ensureHttps } from '../../../assets/utils/reportList/urlPaginationHttpsValidation';
import { ROLE_VALUES } from '../../../constants/customerConstant';
import priorityOptions from '../../../constants/priorityOptions';
import { useAuth } from '../../../contexts/auth/authContext';
import {
  DEFAULT_VALUE,
  INITIAL_VALUE,
  usePartnerContext,
} from '../../../contexts/partner/partnerContext';
import useDecryption from '../../../hooks/cryptography/decryptText';
import useDepartmentData, {
  useLocalizedDepartmentData,
} from '../../../hooks/fetcher/useDepartmentData';
import { useLocalizedReportTypeData } from '../../../hooks/fetcher/useReportTypeData';
import ErrorType from '../../../interfaces/ErrorType';
import LoadingSnackbar from '../../common/Snackbar/loadingSnackbar';
import StackPagination from '../../common/stackPagination';
import EnvelopeIcon from '../../icons/envelopeIcon';

import { TextLink } from '~/components/ui/link';
import { useReportStatusOptions } from '~/hooks/fetcher/useReportStatusOptions';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    height: '56rem',
  },
  tableContainer: {
    maxHeight: '51rem',
    overflow: 'auto',
    border: '1px solid rgba(0, 0, 0, 0.16)',
    borderRadius: '6px',
  },
  tableHead: {
    fontFamily: 'Schibsted Grotesk',
    fontSize: '14px',
    backgroundColor: 'white',
    color: 'grey',
  },
  tableBody: {
    fontSize: '14px',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.24)',
      borderRadius: '4px',
    },
  },
  tableCellHeader: {
    color: 'gray',
    fontSize: '14px',
  },
});

interface ConstrainWrapperProps {
  children: ReactNode | string | undefined;
  sx?: React.CSSProperties;
}

const ConstrainWrapper = ({ children, sx = {} }: ConstrainWrapperProps) => (
  <div
    style={{
      width: '85px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      ...sx,
    }}
    title={typeof children === 'string' ? children : undefined}
  >
    {children}
  </div>
);

// Function to format ISO8601 date to "dd-mm-yyyy" format
const formatDate = (isoTimestamp: string): string => {
  const date = new Date(isoTimestamp);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());

  return `${day}-${month}-${year}`;
};

const getFontStyle = (hasUnreadMessage: boolean) => {
  const defaultStyle = hasUnreadMessage
    ? { color: '#181818', fontWeight: 500 }
    : { fontWeight: 400 };

  return defaultStyle;
};

const getTimeColor = (dueDate: Date, hasUnreadMessage: boolean) => {
  const timeRemaining = dueDate.getTime() - Date.now();
  const hoursRemaining = timeRemaining / (1000 * 60 * 60);

  if (hoursRemaining < 24) {
    return {
      backgroundColor: hasUnreadMessage ? 'transparent' : '#FFF1F1',
      color: hasUnreadMessage ? '#181818' : '#E51D1D',
      padding: '4px 8px 4px 8px',
    };
  } else if (hoursRemaining < 48) {
    return {
      backgroundColor: hasUnreadMessage ? 'transparent' : '#FFFBEB',
      color: hasUnreadMessage ? '#181818' : '#F49D0C',
      padding: '4px 8px 4px 8px',
    };
  } else {
    return {
      color: hasUnreadMessage ? '#181818' : 'inherit',
    };
  }
};

interface CustomerReport {
  reference_id: string;
  reports_type_name: string;
  status: number;
  country_verbose: { code: string; name: string };
  priority: number;
  created_at: string;
  updated_at: string;
  due_date: string;
  final_due_date: string;
  assignee_full_name: string | null;
  assignee: number;
  user_full_name: string;
  description: string;
  company_name: string;
  tenant_has_unread_message: boolean;
  formatted_report_number: string;
}

const ReportsTable = () => {
  const classes = useStyles();

  const { selectedChannel } = useAppSelector(
    (state) => state.intakeChannelData,
  );

  const [customerReportsData, setCustomerReportsData] = useState<
    CustomerReport[]
  >([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [errorState, setErrorState] = useState<ErrorType>({
    errorType: false,
    message: '',
  });

  const { slug } = useAuth();
  const intl = useIntl();
  const { selectedTenant } = usePartnerContext();
  const userType = localStorage.getItem('user_type');
  const [companySlug, setCompanySlug] = useState<string | null>(slug);

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<
    number | undefined | null | string
  >('');
  const [toggleAssignee, setToggleAssignee] = useState<boolean>(false);
  const [departmentName, setDepartmentName] = useState<string>('');
  const [reportsFollowed, setReportsFollowed] = useState<boolean>(false);
  const [tag, setTag] = useState<string>('');

  const { departmentList } = useDepartmentData();
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();
  const { getLocalizedReportType } = useLocalizedReportTypeData();
  const { createToastNotification } = useToastNotificationContext();
  const { statusOptions } = useReportStatusOptions();

  const [count, setCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const { decryptText } = useDecryption();

  const [changePageFetching, setChangePageFetching] = useState(false);

  const localizedNoDueDate = intl.formatMessage({
    defaultMessage: 'No due date set',
  });

  const handlePriorityOptionSelect = (selectedOption: number | null) => {
    setSelectedPriority(selectedOption === null ? '' : selectedOption);
  };

  const handleDateOptionSelect = (selectedDateOption: string) => {
    setSelectedDate(selectedDateOption);
  };

  const handleDepartmentOptionSelect = (selectedDepartmentOption: string) => {
    setDepartmentName(selectedDepartmentOption);
  };

  const getPartnerParams = (tenantId: number): string => {
    if (
      !isNaN(tenantId) &&
      (tenantId === DEFAULT_VALUE || tenantId === INITIAL_VALUE)
    )
      return '';
    return `tenant_id=${tenantId}`;
  };

  const addTenantToParams = (tenantName: string): string => {
    if (
      tenantName &&
      (userType === `${ROLE_VALUES.PARTNER_ADMIN}` ||
        userType === `${ROLE_VALUES.PARTNER_USER}`)
    )
      return `?tenant=${tenantName}`;
    else return '';
  };

  const setDefaultErrorMessage = useCallback(() => {
    setErrorState({
      errorType: false,
      message: intl.formatMessage({
        defaultMessage: 'An unexpected error occurred.',
      }),
    });
  }, [intl]);

  const handlePageChange = async (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): Promise<void> => {
    setFetchLoading(true);
    setChangePageFetching(true);
    setCurrentPage(newPage);

    const urlToFetch = newPage > currentPage ? nextPage : prevPage;

    try {
      if (companySlug && urlToFetch) {
        const customerReportsList = await axiosInstanceForTenantAPI(
          companySlug,
        ).get(ensureHttps(urlToFetch));
        setCustomerReportsData(customerReportsList.data.results);
        setCount(customerReportsList.data.count);
        setNextPage(customerReportsList.data.next);
        setPrevPage(customerReportsList.data.previous);
      }
    } catch (_) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Error fetching reports',
        }),
        'error',
      );
    } finally {
      setChangePageFetching(false);
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const fetchCustomerReportsList = useCallback(async () => {
    setFetchLoading(true);
    try {
      if (companySlug) {
        const customerReportsList = await axiosInstanceForTenantAPI(
          localStorage.getItem('slug'),
        ).get(
          `/report-list?channel_id=${selectedChannel}&department_name=${getLocalizedDepartmentName(
            departmentName,
            'original',
          )}&followed_by_user=${reportsFollowed ? 'True' : 'False'
          }&only_assigned_reports=${toggleAssignee ? 'True' : 'False'
          }&ordering=${selectedDate}&priority=${selectedPriority}&tag=${tag}&${getPartnerParams(
            selectedTenant.id,
          )}`,
        );
        setCurrentPage(0);
        setCustomerReportsData(customerReportsList.data.results);
        setCount(customerReportsList.data.count);
        setNextPage(customerReportsList.data.next);
        setPrevPage(customerReportsList.data.previous);
        setFetchLoading(false);
      }
    } catch (err) {
      if (isRESTAPIError(err)) {
        setErrorState({
          errorType: true,
          message: err.message,
        });
      } else {
        setDefaultErrorMessage();
      }
      setFetchLoading(false);
    }
  }, [
    companySlug,
    toggleAssignee,
    selectedDate,
    selectedPriority,
    departmentName,
    reportsFollowed,
    tag,
    selectedChannel,
    selectedTenant.id,
    setDefaultErrorMessage,
    getLocalizedDepartmentName,
  ]);

  useEffect(() => {
    fetchCustomerReportsList();
  }, [fetchCustomerReportsList]);

  let content = (
    <p>
      <FormattedMessage defaultMessage='No Report Found.' />
    </p>
  );

  if (customerReportsData.length > 0) {
    content = (
      <Table sx={{ margin: 0 }} stickyHeader>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell sx={{ color: 'grey' }}>
              <FormattedMessage defaultMessage='Priority' />
            </TableCell>
            <TableCell
              sx={{
                color: 'grey',
              }}
            >
              <div
                style={{
                  marginTop: '15px',
                  fontSize: '14px',
                  fontFamily: 'Schibsted Grotesk',
                }}
              >
                <FormattedMessage defaultMessage='Report Date' />
              </div>
              <div style={{ fontSize: '12px' }}>(dd-mm-yyyy)</div>
            </TableCell>
            {(userType == `${ROLE_VALUES.PARTNER_ADMIN}` ||
              userType == `${ROLE_VALUES.PARTNER_USER}`) && (
                <TableCell sx={{ color: 'grey' }}>
                  <FormattedMessage defaultMessage='Company' />
                </TableCell>
              )}
            <TableCell sx={{ color: 'grey' }}>
              <FormattedMessage defaultMessage='Report' />
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <FormattedMessage defaultMessage='Report Type' />
            </TableCell>

            <TableCell sx={{ color: 'grey' }}>
              <FormattedMessage defaultMessage='Country' />
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <FormattedMessage defaultMessage='Status' />
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <FormattedMessage defaultMessage='Assignee' />
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <div
                style={{
                  marginTop: '15px',
                  fontSize: '14px',
                  fontFamily: 'Schibsted Grotesk',
                }}
              >
                <FormattedMessage defaultMessage='Last Update' />
              </div>
              <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <div
                style={{
                  marginTop: '15px',
                  fontSize: '14px',
                  fontFamily: 'Schibsted Grotesk',
                }}
              >
                <FormattedMessage defaultMessage='First Due Date' />
              </div>
              <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>{' '}
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <div
                style={{
                  marginTop: '15px',
                  fontSize: '14px',
                  fontFamily: 'Schibsted Grotesk',
                }}
              >
                <FormattedMessage defaultMessage='Final Due Date' />
              </div>
              <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>{' '}
            </TableCell>
            <TableCell sx={{ color: 'grey' }}>
              <div
                style={{
                  marginTop: '15px',
                  fontSize: '14px',
                  fontFamily: 'Schibsted Grotesk',
                }}
              ></div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {/* Mapped Data */}
          {customerReportsData.map((item) => (
            <TableRow key={item.reference_id} hover={true}>
              {/* Priority */}
              <TableCell
                sx={{
                  color: '#000',
                  fontFamily: 'Schibsted Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                <Stack
                  sx={{
                    flexDirection: 'row',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Stack
                    sx={{
                      '&::before': {
                        content: `url(${priorityOptions.find(
                          (status) => status.id === item.priority,
                        )?.color})`,
                        display: 'block',
                        marginRight: '10px',
                        backgroundSize: 'cover',
                      },
                      color: '#181818',
                    }}
                  />
                  <span style={getFontStyle(item.tenant_has_unread_message)}>
                    {
                      priorityOptions.find(
                        (status) => status.id === item.priority,
                      )?.name
                    }
                  </span>
                </Stack>
              </TableCell>

              {/* Date Created */}
              <TableCell
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontStyle: 'normal',
                  lineHeight: 'normal',
                  color: '#181818',
                  ...getFontStyle(item.tenant_has_unread_message),
                }}
              >
                {formatDate(item.created_at)}
              </TableCell>
              {/* company name */}
              {(userType === `${ROLE_VALUES.PARTNER_ADMIN}` ||
                userType === `${ROLE_VALUES.PARTNER_USER}`) && (
                  <TableCell>
                    <ConstrainWrapper>{item.company_name}</ConstrainWrapper>
                  </TableCell>
                )}
              {/* Reference ID */}
              <TableCell>
                <ReportPreview
                  referenceID={item.reference_id}
                  decryptText={decryptText}
                >
                  <TextLink
                    level='body-sm'
                    href={`/customer/reports/${item.reference_id
                      }${addTenantToParams(item.company_name)}`}
                    sx={{
                      color: 'var(--grey-white-background, #878787)',
                      fontFamily: 'Schibsted Grotesk',
                      fontStyle: 'normal',
                      lineHeight: 'normal',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      ...getFontStyle(item.tenant_has_unread_message),
                    }}
                  >
                    {item.formatted_report_number}
                  </TextLink>
                </ReportPreview>
              </TableCell>
              {/* Report Type */}
              <TableCell>
                <ConstrainWrapper
                  sx={{
                    color: '#181818',
                    ...getFontStyle(item.tenant_has_unread_message),
                  }}
                >
                  {getLocalizedReportType(item.reports_type_name)}
                </ConstrainWrapper>
              </TableCell>

              {/* Country */}
              <TableCell>
                <ConstrainWrapper
                  sx={getFontStyle(item.tenant_has_unread_message)}
                >
                  {item.country_verbose?.name ?? (
                    <Typography sx={{ color: 'red', fontSize: '14px' }}>
                      <FormattedMessage defaultMessage='Removed' />
                    </Typography>
                  )}
                </ConstrainWrapper>
              </TableCell>

              {/* Status */}
              <TableCell>
                <ConstrainWrapper
                  sx={getFontStyle(item.tenant_has_unread_message)}
                >
                  {
                    statusOptions.find((status) => status.value === item.status)
                      ?.label
                  }
                </ConstrainWrapper>
              </TableCell>

              {/* Assignee */}
              <TableCell>
                <ConstrainWrapper
                  sx={getFontStyle(item.tenant_has_unread_message)}
                >
                  {item.assignee_full_name ||
                    intl.formatMessage({ defaultMessage: 'To be assigned' })}
                </ConstrainWrapper>
              </TableCell>

              {/* Date Updated */}
              <TableCell
                sx={{
                  color: 'grey',
                  ...getFontStyle(item.tenant_has_unread_message),
                }}
              >
                {formatDate(item.updated_at)}
              </TableCell>

              {/* first Due Date */}
              <TableCell sx={getFontStyle(item.tenant_has_unread_message)}>
                {item.due_date ? formatDate(item.due_date) : localizedNoDueDate}
              </TableCell>

              {/* final Due Date */}
              <TableCell>
                {item.due_date ? (
                  <span
                    style={{
                      ...getTimeColor(
                        new Date(item.final_due_date),
                        item.tenant_has_unread_message,
                      ),
                      fontWeight: item.tenant_has_unread_message ? 500 : 400,
                    }}
                  >
                    {formatDate(item.final_due_date)}
                  </span>
                ) : (
                  localizedNoDueDate
                )}
              </TableCell>
              {/* envelope icon */}
              <TableCell sx={{ fontWeight: 500, pl: 0 }}>
                {item.tenant_has_unread_message ? (
                  <span>
                    <EnvelopeIcon />
                  </span>
                ) : (
                  ''
                )}
              </TableCell>
            </TableRow>
          ))}
          {/* Add more rows here if needed */}
        </TableBody>
      </Table>
    );
  }

  if (customerReportsData.length === 0) {
    content = (
      <>
        <Table sx={{ margin: 0 }} stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Priority' />
              </TableCell>
              <TableCell
                sx={{
                  color: 'grey',
                }}
              >
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='Report Date' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yyyy)</div>
              </TableCell>
              {(userType == `${ROLE_VALUES.PARTNER_ADMIN}` ||
                userType == `${ROLE_VALUES.PARTNER_USER}`) && (
                  <TableCell sx={{ color: 'grey' }}>
                    <FormattedMessage defaultMessage='Company' />
                  </TableCell>
                )}
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Report' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Report Type' />
              </TableCell>

              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Country' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Status' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Assignee' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='Last Update' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yyyy)</div>
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='First Due Date' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>{' '}
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='Final Due Date' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>{' '}
              </TableCell>
            </TableRow>
          </TableHead>
        </Table>
        <Container sx={{ p: 10 }}>
          <Stack direction='row' alignItems='center' justifyContent='center'>
            {fetchLoading ? (
              <CircularProgress
                size={20}
                style={{ marginRight: '8px', color: 'black' }}
              />
            ) : (
              <Typography
                variant='h4'
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontWeight: 600,
                  lineHeight: '0.9375rem',
                  letterSpacing: '0em',
                }}
              >
                <FormattedMessage defaultMessage='No Reports Found' />
              </Typography>
            )}
          </Stack>
        </Container>
      </>
    );
  }

  if (errorState?.errorType) {
    content = (
      <>
        <Table sx={{ margin: 0 }} stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Priority' />
              </TableCell>
              <TableCell
                sx={{
                  color: 'grey',
                }}
              >
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='Report Date' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yyyy)</div>
              </TableCell>
              {(userType == `${ROLE_VALUES.PARTNER_ADMIN}` ||
                userType == `${ROLE_VALUES.PARTNER_USER}`) && (
                  <TableCell sx={{ color: 'grey' }}>
                    <FormattedMessage defaultMessage='Company' />
                  </TableCell>
                )}
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Report' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Report Type' />
              </TableCell>

              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Country' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Status' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <FormattedMessage defaultMessage='Assignee' />
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='Last Update' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yyyy)</div>
              </TableCell>
              <TableCell sx={{ color: 'grey' }}>
                <div
                  style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    fontFamily: 'Schibsted Grotesk',
                  }}
                >
                  <FormattedMessage defaultMessage='Due Date' />
                </div>
                <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>{' '}
              </TableCell>
            </TableRow>
          </TableHead>
        </Table>
        <Container sx={{ p: 10 }}>
          <Stack direction='row' alignItems='center' justifyContent='center'>
            <Typography
              variant='h4'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontWeight: 600,
                lineHeight: '0.9375rem',
                letterSpacing: '0em',
                color: 'red',
              }}
            >
              {errorState?.message}
            </Typography>
          </Stack>
        </Container>
      </>
    );
  }

  return (
    <>
      <LoadingSnackbar stateValue={changePageFetching} />

      {/* Table body */}
      <Box sx={{ width: '100%' }}>
        <Card
          sx={{
            borderRadius: '6px',
            border: '1px solid #00000029',
            padding: '24px 0px',
            boxShadow: '0px 0px 4px 0px #0000000A',
            width: '100%',
            backgroundColor: '#FFF',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ mb: 2, margin: '0px 24px 0px 20px' }}
          >
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '1.125rem',
                fontWeight: 500,
                lineHeight: '1.375rem',
                letterSpacing: '0em',
                textAlign: 'left',
              }}
            >
              <FormattedMessage defaultMessage='List of All Reports' />
            </Typography>
            <FilterButtonWithPopOver
              onOptionSelect={handlePriorityOptionSelect}
              onDateOptionSelect={handleDateOptionSelect}
              onDepartmentOptionSelect={handleDepartmentOptionSelect}
              departmentList={departmentList}
              setToggleAssignee={setToggleAssignee}
              toggleAssignee={toggleAssignee}
              setReportsFollowed={setReportsFollowed}
              reportsFollowed={reportsFollowed}
              setTag={setTag}
            />
          </Stack>

          {/* Table */}
          <TableContainer>
            <div style={{ maxHeight: '800px', overflowY: 'auto' }}>
              {content}
            </div>
          </TableContainer>
          {/* Table */}
        </Card>

        {/* Table Pagination */}
        <StackPagination
          count={count}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </Box>
    </>
  );
};

export default ReportsTable;
